if (!customElements.get('sl003-experience')) {
  customElements.define('sl003-experience', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.states = new Map();
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const state = this.states.get(entry.target);
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          this.syncVideo(entry.target, state);
        }
      }, { threshold: [0, 0.35, 0.7] });
      this.querySelectorAll('[data-auto-video]').forEach(video => {
        const state = { visible: false, paused: this.motion.matches || Boolean(navigator.connection?.saveData), programmatic: false };
        this.states.set(video, state);
        video.muted = true;
        if (state.paused && video.controls) this.loadVideo(video);
        this.observer.observe(video);
        video.addEventListener('pause', () => {
          if (video.controls && state.visible && !state.programmatic && !document.hidden && !this.dialog.open) state.paused = true;
          this.updateToggle(video);
        }, { signal });
        video.addEventListener('play', () => { state.paused = false; this.updateToggle(video); }, { signal });
      });
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-scene]');
        if (tab) this.selectScene(tab);
        const full = event.target.closest('[data-full-video]');
        if (full) this.openVideo(full);
        if (event.target.closest('[data-close-video]')) this.dialog.close();
        const toggle = event.target.closest('.sl003-preview-toggle');
        if (toggle) {
          const video = toggle.closest('.sl003-review-media').querySelector('video');
          const state = this.states.get(video);
          state.paused = !video.paused;
          this.syncVideo(video, state, true);
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-scene]');
        if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = [...this.querySelectorAll('[data-scene]')];
        let index = tabs.indexOf(tab);
        index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        this.selectScene(tabs[index]);
        tabs[index].focus();
      }, { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close(); } }, { signal });
      this.dialog.addEventListener('close', () => {
        const video = this.dialog.querySelector('video');
        if (video) video.pause();
        this.dialog.querySelector('.sl003-dialog-player').replaceChildren();
        document.documentElement.style.overflow = this.previousOverflow;
        this.opener?.focus({ preventScroll: true });
        this.states.forEach((state, item) => this.syncVideo(item, state));
      }, { signal });
      document.addEventListener('visibilitychange', () => this.states.forEach((state, video) => this.syncVideo(video, state)), { signal });
      this.querySelectorAll('.sl003-video-load').forEach(details => details.addEventListener('toggle', () => {
        const template = details.querySelector('template');
        if (details.open && template) template.replaceWith(template.content.cloneNode(true));
      }, { signal }));
    }
    selectScene(tab) {
      this.querySelectorAll('[data-scene]').forEach(item => { const selected = item === tab; item.setAttribute('aria-selected', String(selected)); item.tabIndex = selected ? 0 : -1; });
      this.querySelectorAll('[data-scene-panel]').forEach(panel => { panel.hidden = panel.dataset.scenePanel !== tab.dataset.scene; });
    }
    syncVideo(video, state, explicit = false) {
      if (state.visible && !state.paused && !document.hidden && !this.dialog.open || explicit && !state.paused) {
        this.loadVideo(video);
        state.programmatic = false;
        video.play().catch(() => { this.updateToggle(video); });
      } else {
        state.programmatic = true;
        video.pause();
      }
      this.updateToggle(video);
    }
    loadVideo(video) {
      if (video.dataset.loaded) return;
      const source = [...video.querySelectorAll('source[data-src]')].find(item => !item.media || window.matchMedia(item.media).matches);
      if (!source) return;
      video.src = source.dataset.src;
      video.dataset.loaded = 'true';
      video.load();
    }
    updateToggle(video) {
      const toggle = video.closest('.sl003-review-media')?.querySelector('.sl003-preview-toggle');
      if (!toggle) return;
      toggle.setAttribute('aria-label', video.paused ? 'Play preview' : 'Pause preview');
      toggle.title = video.paused ? 'Play preview' : 'Pause preview';
      toggle.querySelector('.sl003-pause-icon').hidden = video.paused;
      toggle.querySelector('.sl003-play-icon').hidden = !video.paused;
    }
    openVideo(button) {
      const template = this.querySelector('#' + CSS.escape(button.dataset.fullVideo));
      if (!template) return;
      this.opener = button;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.querySelector('h2').textContent = template.dataset.title;
      this.dialog.querySelector('.sl003-dialog-player').replaceChildren(template.content.cloneNode(true));
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.states.forEach((state, video) => this.syncVideo(video, state));
      const fullVideo = this.dialog.querySelector('video');
      fullVideo.setAttribute('aria-label', template.dataset.title);
      fullVideo.muted = false;
      fullVideo.currentTime = 0;
      fullVideo.play().catch(() => {});
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.observer?.disconnect();
      this.states?.forEach((state, video) => video.pause());
      this.abort?.abort();
      this.started = false;
    }
  });
}
